import { useState } from "react";
import { Check, Copy, CheckCircle2, CircleDashed, ChevronDown } from "lucide-react";
import { toast } from "sonner";

export const CopyBlock = ({ text, testid, label }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setDone(true); toast.success(`${label || "Text"} copied`); setTimeout(() => setDone(false), 1500); }
    catch { toast.error("Your browser blocked copying. Select the text and copy it manually"); }
  };
  return (
    <div className="relative group" data-testid={testid}>
      <pre style={{ fontVariantLigatures: "none" }} className="bg-black/60 border border-white/10 p-3 pr-12 font-mono text-[11px] text-white/85 overflow-x-auto whitespace-pre-wrap break-all leading-relaxed">{text}</pre>
      <button onClick={copy} className="absolute top-2 right-2 p-1.5 border border-white/15 bg-black/80 text-white/60 hover:text-brand hover:border-brand transition-colors" data-testid={`${testid}-copy`} aria-label="Copy">
        {done ? <Check className="w-3.5 h-3.5 text-brand" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
};

export const CheckRow = ({ c }) => (
  <li className="flex gap-2 text-xs" data-testid={`check-${c.id}`}>
    {c.ok ? <CheckCircle2 className="w-4 h-4 text-brand shrink-0" /> : <CircleDashed className="w-4 h-4 text-amber-300 shrink-0" />}
    <span className={c.ok ? "text-white/80" : "text-white/55"}>{c.detail}</span>
  </li>
);

const WAITING = ["webhook_received", "embed_loaded", "test_payment", "test_verification", "test_linked"];
const label = (s) => s.done ? "CONNECTED" : s.checks.every((c) => c.ok || WAITING.includes(c.id)) && s.checks.some((c) => !c.ok && WAITING.includes(c.id)) && s.id !== "embed" && s.id !== "test" ? "WAITING FOR FIRST EVENT" : "ACTION NEEDED";

export const StepCard = ({ n, step, open, toggle, optional, children }) => (
  <section className={`panel overflow-hidden transition-colors ${step.done ? "border-brand/30" : ""}`} data-testid={`step-${step.id}`}>
    <button onClick={toggle} className="w-full flex items-center gap-4 p-5 text-left" data-testid={`step-${step.id}-toggle`}>
      <span className={`w-9 h-9 grid place-items-center font-mono text-sm font-bold border ${step.done ? "bg-brand text-black border-brand" : "border-white/20 text-white/70"}`}>
        {step.done ? <Check className="w-4 h-4" /> : n}
      </span>
      <span className="flex-1">
        <span className="font-semibold">{step.title}</span>
        {optional && <span className="ml-2 font-mono text-[10px] text-white/40">OPTIONAL</span>}
        <span className={`block font-mono text-[10px] mt-0.5 ${step.done ? "text-brand" : "text-amber-300/80"}`} data-testid={`step-${step.id}-status`}>{label(step)}</span>
      </span>
      <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
    {open && (
      <div className="px-5 pb-6 pl-[4.5rem] space-y-4 animate-in fade-in slide-in-from-top-1 duration-300">
        {children}
        <ul className="space-y-1.5 border-t border-white/10 pt-4">{step.checks.map((c) => <CheckRow key={c.id} c={c} />)}</ul>
      </div>
    )}
  </section>
);

export const Do = ({ children, start }) => <ol start={start} className="list-decimal pl-5 space-y-1.5 text-sm text-white/75 marker:text-brand marker:font-mono">{children}</ol>;
